import React, { useCallback, useEffect, useRef } from 'react';

export type SignaturePadValue = {
  dataUrl: string;
  isEmpty: boolean;
};

export function SignaturePad({
  heightPx = 220,
  onChange,
}: {
  heightPx?: number;
  onChange: (value: SignaturePadValue) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const hasInkRef = useRef(false);

  const publish = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (!hasInkRef.current) {
      onChange({ dataUrl: '', isEmpty: true });
      return;
    }

    onChange({
      dataUrl: canvas.toDataURL('image/png'),
      isEmpty: false,
    });
  }, [onChange]);

  const syncCanvasSize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    if (!Number.isFinite(rect.width) || !Number.isFinite(rect.height) || rect.width <= 0 || rect.height <= 0) {
      return;
    }

    const dpr = typeof window !== 'undefined' && window.devicePixelRatio ? window.devicePixelRatio : 1;
    const nextWidth = Math.round(rect.width * dpr);
    const nextHeight = Math.round(rect.height * dpr);

    if (canvas.width === nextWidth && canvas.height === nextHeight) return;

    canvas.width = nextWidth;
    canvas.height = nextHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827'; // gray-900
    ctx.lineWidth = 2.25;

    // Resizing clears the drawing; treat as empty.
    ctx.clearRect(0, 0, rect.width, rect.height);
    hasInkRef.current = false;
    publish();
  }, [publish]);

  useEffect(() => {
    syncCanvasSize();

    const onResize = () => syncCanvasSize();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [syncCanvasSize]);

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, rect.width, rect.height);
    hasInkRef.current = false;
    drawingRef.current = false;
    lastPointRef.current = null;

    publish();
  };

  const getPoint = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = e.currentTarget;
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const drawLine = (from: { x: number; y: number }, to: { x: number; y: number }) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-gray-700">Customer signature</div>
        <button
          type="button"
          onClick={clear}
          className="text-sm font-semibold text-blue-700 hover:underline"
        >
          Clear
        </button>
      </div>

      <div className="rounded-2xl border border-gray-300 bg-white overflow-hidden">
        <canvas
          ref={canvasRef}
          className="w-full touch-none"
          style={{ height: `${heightPx}px` }}
          onPointerDown={(e) => {
            syncCanvasSize();

            drawingRef.current = true;
            lastPointRef.current = getPoint(e);

            try {
              e.currentTarget.setPointerCapture(e.pointerId);
            } catch {
              // ignore
            }
          }}
          onPointerMove={(e) => {
            if (!drawingRef.current) return;

            const last = lastPointRef.current;
            const next = getPoint(e);
            if (!last) {
              lastPointRef.current = next;
              return;
            }

            drawLine(last, next);
            lastPointRef.current = next;
            hasInkRef.current = true;
          }}
          onPointerUp={() => {
            if (!drawingRef.current) return;
            drawingRef.current = false;
            lastPointRef.current = null;
            publish();
          }}
          onPointerCancel={() => {
            if (!drawingRef.current) return;
            drawingRef.current = false;
            lastPointRef.current = null;
            publish();
          }}
        />
      </div>

      <div className="text-xs text-gray-500">Sign with your finger or Apple Pencil.</div>
    </div>
  );
}
